import { useCallback, useEffect, useState } from 'react';

/**
 * Minimal history router.
 *
 * The app has five screens and one parameterised route; a routing library would
 * be more machinery than the problem needs. This handles pushes, back/forward,
 * and same-path replaces.
 */

export type Route =
  | { name: 'landing' }
  | { name: 'signin' }
  | { name: 'signup' }
  | { name: 'home' }
  | { name: 'settings' }
  | { name: 'board'; id: string }
  | { name: 'public'; slug: string }
  | { name: 'invite'; token: string }
  | { name: 'demo' }
  | { name: 'forgot' }
  | { name: 'privacy' }
  | { name: 'reset'; token: string }
  | { name: 'notfound' };

export function parsePath(pathname: string): Route {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return { name: 'landing' };
  if (path === '/signin') return { name: 'signin' };
  if (path === '/signup') return { name: 'signup' };
  if (path === '/app') return { name: 'home' };
  if (path === '/settings') return { name: 'settings' };
  if (path === '/forgot') return { name: 'forgot' };
  if (path === '/privacy') return { name: 'privacy' };
  if (path === '/demo') return { name: 'demo' };

  const board = /^\/board\/([A-Za-z0-9_-]{1,64})$/.exec(path);
  if (board) return { name: 'board', id: board[1] };

  const shared = /^\/b\/([A-Za-z0-9_-]{1,80})$/.exec(path);
  if (shared) return { name: 'public', slug: shared[1] };

  const invite = /^\/invite\/([A-Za-z0-9_-]{10,200})$/.exec(path);
  if (invite) return { name: 'invite', token: invite[1] };

  const reset = /^\/reset\/([A-Za-z0-9_-]{10,200})$/.exec(path);
  if (reset) return { name: 'reset', token: reset[1] };

  return { name: 'notfound' };
}

export function routePath(route: Route): string {
  switch (route.name) {
    case 'landing':
      return '/';
    case 'signin':
      return '/signin';
    case 'signup':
      return '/signup';
    case 'home':
      return '/app';
    case 'settings':
      return '/settings';
    case 'board':
      return `/board/${route.id}`;
    case 'public':
      return `/b/${route.slug}`;
    case 'invite':
      return `/invite/${route.token}`;
    case 'demo':
      return '/demo';
    case 'forgot':
      return '/forgot';
    case 'privacy':
      return '/privacy';
    case 'reset':
      return `/reset/${route.token}`;
    case 'notfound':
      return '/404';
  }
}

export function useRouter() {
  const [route, setRoute] = useState<Route>(() => parsePath(location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parsePath(location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((next: Route, options?: { replace?: boolean }) => {
    const path = routePath(next);
    if (path === location.pathname && !options?.replace) return;
    if (options?.replace) history.replaceState(null, '', path);
    else history.pushState(null, '', path);
    setRoute(next);
    // A fresh screen should start at the top, like a real navigation.
    window.scrollTo(0, 0);
  }, []);

  return { route, navigate };
}

export type Navigate = ReturnType<typeof useRouter>['navigate'];
